import React, { useState } from 'react';
import './ContactPage.css';

interface ContactPageProps {
  onNavigateToChatbot: () => void;
}

const ContactPage: React.FC<ContactPageProps> = ({ onNavigateToChatbot }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Thank you for your message! Our team will get back to you within 24 hours.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <main className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-content">
          <h1>Contact PolicyPal</h1>
          <p>We're here to help with all your insurance needs. Get in touch with our expert team today.</p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-form-section">
              <h2>Send Us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="quote">Get a Quote</option>
                      <option value="claim">File a Claim</option>
                      <option value="support">Customer Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help you..."
                    required
                  ></textarea>
                </div>

                <button type="submit" className="submit-btn">Send Message</button>
              </form>
            </div>

            <div className="contact-info-section">
              <h2>Get in Touch</h2>
              
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">📞</div>
                  <div className="method-details">
                    <h3>Phone</h3>
                    <p>1-800-POLICY-1</p>
                    <span>Mon-Fri: 8AM-8PM EST</span>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">📧</div>
                  <div className="method-details">
                    <h3>Email</h3>
                    <p>support@policypal.com</p>
                    <span>We respond within 24 hours</span>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">💬</div>
                  <div className="method-details">
                    <h3>Live Chat</h3>
                    <p>Available 24/7</p>
                    <button 
                      className="chat-button"
                      onClick={onNavigateToChatbot}
                    >
                      Start Chat
                    </button>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">📍</div>
                  <div className="method-details">
                    <h3>Office</h3>
                    <p>123 Insurance Way<br />Suite 100<br />New York, NY 10001</p>
                  </div>
                </div>
              </div>

              <div className="emergency-contact">
                <h3>🚨 Emergency Claims</h3>
                <p>For urgent claims after hours:</p>
                <p className="emergency-number">1-800-EMERGENCY</p>
                <span>Available 24/7/365</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How quickly will I receive a quote?</h3>
              <p>Most quotes are generated instantly online. Complex cases may take up to 24 hours for our underwriters to review.</p>
            </div>

            <div className="faq-item">
              <h3>What information do I need for a quote?</h3>
              <p>Basic personal information, details about what you want to insure, and your coverage preferences. Our quote form will guide you through everything needed.</p>
            </div>

            <div className="faq-item">
              <h3>How do I file a claim?</h3>
              <p>You can file a claim online through our website, via our mobile app, or by calling our claims hotline. We'll guide you through the entire process.</p>
            </div>

            <div className="faq-item">
              <h3>Can I manage my policy online?</h3>
              <p>Yes! You can view your policy, make payments, update information, and download documents through your online account portal.</p>
            </div>

            <div className="faq-item">
              <h3>What types of insurance do you offer?</h3>
              <p>We offer auto, home, renters, life, health, and business insurance. We can also help with specialty coverage for unique needs.</p>
            </div>

            <div className="faq-item">
              <h3>Do you offer discounts?</h3>
              <p>Yes! We offer various discounts including multi-policy bundling, safe driver discounts, home security discounts, and more.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Office Hours */}
      <section className="hours-section">
        <div className="container">
          <div className="hours-content">
            <h2>Business Hours</h2>
            <div className="hours-grid">
              <div className="hours-item">
                <h3>Customer Service</h3>
                <div className="hours-details">
                  <p>Monday - Friday: 8:00 AM - 8:00 PM EST</p>
                  <p>Saturday: 9:00 AM - 5:00 PM EST</p>
                  <p>Sunday: 10:00 AM - 4:00 PM EST</p>
                </div>
              </div>

              <div className="hours-item">
                <h3>Claims Department</h3>
                <div className="hours-details">
                  <p>Available 24/7/365</p>
                  <p>Emergency claims always accepted</p>
                </div>
              </div>

              <div className="hours-item">
                <h3>Sales & Quotes</h3>
                <div className="hours-details">
                  <p>Monday - Friday: 8:00 AM - 9:00 PM EST</p>
                  <p>Saturday: 9:00 AM - 6:00 PM EST</p>
                  <p>Sunday: 10:00 AM - 5:00 PM EST</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;